/** Accessible playback controls and attribution for direct-hosted KLIPY GIFs. */

import { useEffect, useState, type SyntheticEvent } from 'react';
import { IconPlayerPauseFilled, IconPlayerPlayFilled } from '@tabler/icons-react';
import { KlipyAttribution } from './KlipyAttribution';

export interface KlipyMediaProps {
  imageUrl: string;
  imageStillUrl: string;
  alt: string;
  className?: string;
  onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onError?: (event: SyntheticEvent<HTMLImageElement>) => void;
}

/** Render one animated GIF with a static reduced-motion and pause fallback. */
export function KlipyMedia({ imageUrl, imageStillUrl, alt, className = '', onLoad, onError }: KlipyMediaProps) {
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setPaused(typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, [imageUrl]);

  return (
    <div className="relative w-fit max-w-full">
      <img src={paused ? imageStillUrl : imageUrl} alt={alt} className={className}
        onLoad={onLoad} onError={onError} />
      <div role="group" aria-label="GIF controls" className="absolute bottom-2 right-2 z-10 flex items-center gap-2">
        <KlipyAttribution />
        <button type="button" aria-label={paused ? 'Play GIF' : 'Pause GIF'}
          onClick={() => setPaused((value) => !value)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-black/60 text-white backdrop-blur-sm transition hover:bg-black/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt">
          {paused ? <IconPlayerPlayFilled aria-hidden="true" size={15} /> : <IconPlayerPauseFilled aria-hidden="true" size={15} />}
        </button>
      </div>
    </div>
  );
}
